import React, { useEffect, useState } from "react";
import { useLang } from "../LangContext";
import { useTenant } from "../TenantContext";
import { HashLink } from "../HashLink";
import axios from "axios";
import { ArrowLeft, MapPin, Bookmark, UtensilsCrossed, BedDouble, Compass, Landmark } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const catConfig = {
    all: { icon: Bookmark, key: "tab_all", value: "all" },
    attrazione: { icon: Landmark, key: "tab_attractions", value: "attrazione" },
    mangiare: { icon: UtensilsCrossed, key: "tab_food", value: "mangiare" },
    dormire: { icon: BedDouble, key: "tab_stay", value: "dormire" },
    itinerario: { icon: Compass, key: "tab_itin", value: "itinerario" },
};
const orderedCats = ["all", "attrazione", "mangiare", "dormire", "itinerario"];

export default function AttrazioniPage() {
    const { t, lang } = useLang();
    const { slug } = useTenant();
    const [items, setItems] = useState([]);
    const [active, setActive] = useState("all");

    useEffect(() => {
        window.scrollTo({ top: 0 });
        axios.get(`${API}/attractions`, { params: { borgo: slug } }).then((r) => setItems(r.data)).catch(() => {});
    }, [slug]);

    const visible = items.filter((it) => {
        if (active === "all") return true;
        if (active === "attrazione") return it.category === "attrazione" || it.category === "natura";
        return it.category === active;
    });

    return (
        <div className="pt-32 pb-24 px-6 lg:px-10 min-h-screen bg-stone-50" data-testid="attrazioni-page">
            <div className="max-w-7xl mx-auto">
                <div className="text-center mb-12">
                    <div className="text-xs font-semibold tracking-[0.3em] uppercase text-amber-700 mb-3">
                        {lang === "it" ? "GUIDA COMPLETA" : "COMPLETE GUIDE"}
                    </div>
                    <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-semibold text-stone-900">
                        {t.attr_page_title}
                    </h1>
                    <p className="mt-4 text-lg text-stone-600">{t.attr_page_sub}</p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-12" data-testid="attrazioni-tabs">
                    {orderedCats.map((c) => {
                        const cfg = catConfig[c];
                        const Icon = cfg.icon;
                        const isActive = active === c;
                        return (
                            <button
                                key={c}
                                onClick={() => setActive(c)}
                                className={`flex items-center gap-2 px-5 md:px-6 py-2.5 rounded-full font-semibold text-sm border transition-all ${
                                    isActive
                                        ? "bg-amber-600 text-white border-amber-600 shadow-md"
                                        : "bg-white text-stone-700 border-stone-200 hover:border-amber-500 hover:text-amber-700"
                                }`}
                                data-testid={`tab-${c}`}
                            >
                                <Icon size={16} />
                                {t[cfg.key]}
                            </button>
                        );
                    })}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                    {visible.map((it, i) => (
                        <article
                            key={it.id}
                            className="card-hover bg-white border border-stone-200 rounded-2xl overflow-hidden"
                            data-testid={`attraction-item-${it.id}`}
                        >
                            <div className="aspect-[4/3] overflow-hidden bg-amber-50 relative">
                                {it.image ? (
                                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <Landmark size={40} className="text-amber-400" />
                                    </div>
                                )}
                            </div>
                            <div className="p-6">
                                <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-100 mb-3">
                                    {it.type_label}
                                </span>
                                <h3 className="font-serif text-2xl font-semibold text-stone-900 mb-2">{it.name}</h3>
                                <p className="text-stone-600 text-sm leading-relaxed mb-4">{it.description}</p>
                                {it.map_url && (
                                    <a
                                        href={it.map_url}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 text-amber-700 font-semibold text-sm hover:text-amber-800 transition-colors"
                                        data-testid={`map-link-${it.id}`}
                                    >
                                        <MapPin size={16} />
                                        {t.map_link}
                                    </a>
                                )}
                            </div>
                        </article>
                    ))}
                </div>

                <div className="text-center mt-16">
                    <HashLink
                        to="/"
                        className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-amber-600 text-white font-semibold hover:bg-amber-700 hover:-translate-y-0.5 transition-all shadow-lg"
                        data-testid="back-home-btn-attr"
                    >
                        <ArrowLeft size={18} />
                        {t.back_home}
                    </HashLink>
                </div>
            </div>
        </div>
    );
}
